/**
 * TariffCardView — витринная карточка тарифа, ЕДИНАЯ для /tariffs и ЛК
 * (ступень 2 переиспользования, решение владельца 2026-08-07).
 *
 * Вся ПОДАЧА живёт здесь: бейдж токенов (с разовыми credits_one_time),
 * цена и период, подпись автосписания, перки со scope-метками, раскладка
 * card_blocks с кастом-боксами. Страницы передают только КОНТЕКСТ:
 * бейджи (best/«Ваш тариф»), кнопку (текст + обработчик), мобильный
 * аккордеон и viewerKind — чьими глазами показывать both-тариф
 * (ЛК разового покупателя видит свой пакет, а не подписочную подачу).
 */
import { Fragment, useEffect, useState } from 'react'
import type React from 'react'
import { Coin } from '../icons/Coin.tsx'
import ModelIcon from './ModelIcon'
import { detectBrand } from '../data/brands'
import {
	INCLUDED_CATEGORY_LABELS,
	carryoverBenefit,
	characterFeature,
	discountLines,
	formatCredits,
	formatPrice,
	includedShort,
	parseDescription,
	periodLabel,
} from '../data/tariffShowcase'
import type { CardBlock, Tariff } from '../data/tariffShowcase'

// Маркеры строк кастом-бокса: глиф по payload.marker; неизвестное значение
// из данных = 'plus' (легаси-боксы без настройки). '' = строки без маркера.
const CUSTOM_MARKER_GLYPHS: Record<string, string> = {
	plus: '+', dot: '•', check: '✓', sparkle: '✦', arrow: '→', none: '',
}

// Компакт-имя модели для ховер-ячейки «Токенов хватит на»: скобочные режимы
// сжимаются в аббревиатуру («Kling 2.6 (Image to Video)» → «Kling 2.6 i2v»)
// — тот же язык, каким владелец пишет имена в текстах карточек.
function shortModelName(name: string): string {
	return name
		.replace(/\(\s*image\s*to\s*video\s*\)/i, 'i2v')
		.replace(/\(\s*text\s*to\s*video\s*\)/i, 't2v')
		.replace(/\(\s*image\s*to\s*image\s*\)/i, 'i2i')
		.replace(/\(\s*text\s*to\s*image\s*\)/i, 't2i')
		.replace(/\s{2,}/g, ' ')
		.trim()
}

export interface TariffCardViewProps {
	tariff: Tariff
	/** База вариации пака: зачёркнутая цена и «+N токенов» */
	baseOf?: Tariff | null
	/** Промо-выделение карточки (glow + бейдж) */
	isBest?: boolean
	badgeText?: string
	/** «Ваш тариф»: бейдж вытесняет промо */
	isOwned?: boolean
	isLower?: boolean
	buttonLabel: string
	buttonDisabled?: boolean
	buttonBest?: boolean
	onButtonClick?: () => void
	/** Подпись «списание ежемесячно» под ценой (витрина с рекуррентом) */
	recurrentEnabled?: boolean
	/** Аккордеон: витрина включает его на мобиле, ЛК — на всех ширинах
	 *  (состав там раскрывается панелью поверх соседних блоков) */
	collapsible?: boolean
	isOpen?: boolean
	onToggle?: () => void
	/** Off-состояние блока бесплатных: «Доступны с тарифа X» */
	emptyFreeNote?: string | null
	/** Чьими глазами показывать both-тариф: ЛК разового покупателя передаёт
	 *  'one_time' — бейдж токенов и подача считаются от разовых условий */
	viewerKind?: 'subscription' | 'one_time'
	/** Точная подпись периода вместо вычисленной («· пакет до 5 сент» в ЛК) */
	periodOverride?: string | null
	/** Инлайн-стиль корня — витрина передаёт --ti для каскадной анимации */
	style?: React.CSSProperties
	/** Статусная строка ЛК (чипы «Ваш тариф» / «разовый») — первый элемент
	 *  карточки, вместо торчащего над кромкой витринного бейджа */
	statusChip?: React.ReactNode
}

export default function TariffCardView({
	tariff: t,
	baseOf = null,
	isBest = false,
	badgeText = '',
	isOwned = false,
	isLower = false,
	buttonLabel,
	buttonDisabled = false,
	buttonBest = false,
	onButtonClick,
	recurrentEnabled = false,
	collapsible = false,
	isOpen = false,
	onToggle,
	emptyFreeNote = null,
	viewerKind,
	periodOverride = null,
	style,
	statusChip,
}: TariffCardViewProps) {
	const isFree = t.price === 0
	const { hint, features } = parseDescription(t.description)

	// Поповер аккордеон-блоков («Дополнительно», «Какие модели входят»):
	// раскрытие ПОВЕРХ своей карточки, якорь — кнопка в футере; сетку
	// тарифов не двигает (решение владельца 2026-08-08). Открыт максимум
	// один на карточку; Escape/клик-вне/повторный клик закрывают.
	const [openPop, setOpenPop] = useState<string | null>(null)
	useEffect(() => {
		if (!openPop) return
		const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpenPop(null) }
		window.addEventListener('keydown', onKey)
		return () => window.removeEventListener('keydown', onKey)
	}, [openPop])

	// Режим подачи: жёсткий режим тарифа перебивает глаза зрителя.
	const oneTimeOnly = t.purchase_mode === 'one_time'
	const subOnly = t.purchase_mode === 'subscription'
	const asOneTime = oneTimeOnly || (!subOnly && viewerKind === 'one_time')
	const scopeAllows = (sc?: string | null) => {
		const v = sc || 'any'
		if (oneTimeOnly || viewerKind === 'one_time') return v === 'any' || v === 'one_time'
		if (subOnly || viewerKind === 'subscription') return v === 'any' || v === 'subscription'
		return true
	}
	const showScopeTags = !oneTimeOnly && !subOnly && !viewerKind
	const scopeTagHtml = (sc?: string | null) =>
		!showScopeTags ? ''
			: sc === 'subscription' ? ' <em class="tariff-scope-tag">по подписке</em>'
			: sc === 'one_time' ? ' <em class="tariff-scope-tag">разовым</em>'
			: ''

	const itemFeatures = t.items
		.filter(item => item.is_enabled && scopeAllows(item.applies_to))
		.map(item =>
			(item.custom_description ||
			(item.model_id ? `${item.adapter_name} / ${item.model_id}` : item.adapter_name || item.item_type))
			+ scopeTagHtml(item.applies_to),
		)

	// Разовый пакет не обещает перенос: без продления он не срабатывает.
	// Если админ разместил системную строку («Перенос · авто» / «Персонажи ·
	// авто») в кастом-боксе — авто-строку из перков убираем: одно место
	// истины, дубли невозможны (вариант B владельца, 2026-08-08).
	const placedIn = (kind: 'carryover' | 'characters') => (t.card_blocks ?? []).some(
		(b) => b.kind === 'custom' && (b.payload?.lines ?? []).some((l) => l.kind === kind),
	)
	const carryoverPlaced = placedIn('carryover')
	const carryover = asOneTime || carryoverPlaced ? null : carryoverBenefit(t)
	// Эффективные значения персонажей ПО ПОДАЧЕ: разовая берёт свои one_time
	// (null = как у подписки) — то самое «разветвление» условий.
	const charLimitEff = asOneTime && t.character_limit_one_time != null
		? t.character_limit_one_time
		: t.character_limit ?? 0
	const charFreeEff = asOneTime && t.character_limit_one_time != null
		? t.character_free_limit_one_time ?? t.character_free_limit ?? 0
		: t.character_free_limit ?? 0
	const charactersPlaced = placedIn('characters')
	const characters = charactersPlaced ? null : characterFeature(charLimitEff, charFreeEff)
	const scopedDiscounts = (t.model_discounts ?? []).filter((d) => scopeAllows(d.applies_to))
	const allFeatures = [...features, ...(carryover ? [carryover] : []), ...(characters ? [characters] : []), ...discountLines(scopedDiscounts), ...itemFeatures]
	if (allFeatures.length === 0) allFeatures.push('Доступ ко всем нейросетям')

	const shownCredits = asOneTime && t.credits_one_time != null ? t.credits_one_time : t.credits
	const tokenGain = baseOf && t.credits > baseOf.credits ? t.credits - baseOf.credits : 0

	const handleToggle = (e?: React.MouseEvent) => {
		if (!collapsible) return
		// Клик по содержимому раскрытых фич карточку не сворачивает: юзер
		// читает или скроллит панель, случайное схлопывание — потеря места.
		if (e && isOpen && (e.target as HTMLElement).closest('.tariff-features')) return
		onToggle?.()
	}

	return (
		<div
			style={style}
			className={`tariff-card ${isBest ? 'best' : ''} glow-border-hover ${collapsible && isOpen ? 'is-open' : ''} ${isLower ? 'is-lower' : ''} ${isOwned ? 'is-current' : ''} ${openPop ? 'has-popover' : ''}`}
			onClick={handleToggle}
			role={collapsible ? 'button' : undefined}
			tabIndex={collapsible ? 0 : undefined}
			aria-expanded={collapsible ? isOpen : undefined}
			onKeyDown={(e) => {
				if (!collapsible) return
				if (e.key === 'Enter' || e.key === ' ') {
					e.preventDefault()
					onToggle?.()
				}
			}}
		>
			{/* «Ваш тариф» вытесняет промо-бейдж: свой тариф юзеру не продаём */}
			{isOwned ? (
				<div className="tariff-badge current">Ваш тариф</div>
			) : (
				isBest && <div className="tariff-badge">{badgeText}</div>
			)}
			{statusChip}
			<div className="tariff-top">
				<div className="tariff-top-left">
					<span className="tariff-name">{t.name}</span>
					{shownCredits > 0 && (
						<span className={`tariff-token-badge${tokenGain ? ' tariff-token-badge--up' : ''}`}>
							{formatCredits(shownCredits)} токенов
							{tokenGain > 0 && <em className="tariff-token-gain">+{formatCredits(tokenGain)}</em>}
							<Coin />
						</span>
					)}
				</div>

				{collapsible && (
					<div className={`tariff-chevron ${isOpen ? 'open' : ''}`}>
						<svg width="18" height="18" viewBox="0 0 24 24" fill="none">
							<path
								d="M6 9L12 15L18 9"
								stroke="currentColor"
								strokeWidth="2"
								strokeLinecap="round"
								strokeLinejoin="round"
							/>
						</svg>
					</div>
				)}
			</div>

			<div className="tariff-price-block">
				<span className="tariff-price">{formatPrice(t.price)}</span>
				{baseOf && baseOf.price !== t.price && (
					<span className="tariff-old-price">{formatPrice(baseOf.price)}</span>
				)}
				{!isFree && (
					<span className="tariff-period">
						{periodOverride ?? (asOneTime ? `· пакет на ${t.token_expire_days ?? 30} дней` : periodLabel(t.token_expire_days))}
					</span>
				)}
			</div>
			{!isFree && recurrentEnabled && !asOneTime && (
				<div className="tariff-recurrent-note">
					{t.purchase_mode === 'subscription' ? 'Только по подписке — ' : 'При подписке '}
					{(t.token_expire_days ?? 30) >= 28 && (t.token_expire_days ?? 30) <= 31
						? 'списание ежемесячно'
						: `списание каждые ${t.token_expire_days} дней`}
				</div>
			)}
			{/* periodOverride глушит продажную приписку: в ЛК срок уже в подписи
			    цены («· пакет до 5 сент»), а «разовость» акцентирует чип выше */}
			{!isFree && asOneTime && !periodOverride && (
				<div className="tariff-recurrent-note">Разовый платёж, без автопродления</div>
			)}

			<div className="tariff-action-block">
				{hint && <div className="tariff-hint">{hint}</div>}
				<button
					className={`tariff-btn ${buttonBest ? 'best' : ''} ${buttonDisabled ? 'disabled' : ''}`}
					disabled={buttonDisabled}
					onClick={(e) => {
						e.stopPropagation()
						onButtonClick?.()
					}}
				>
					{buttonLabel}
				</button>
			</div>

			<div className="tariff-features">
				{/* Композиция карточки: РАСКЛАДКОЙ управляет админ (card_blocks —
				    порядок, видимость, кастом-боксы). Пустая раскладка = дефолт:
				    хватит-на → бесплатные → перки → состав. */}
				{(() => {
					const enoughBlock = (t.enough?.length ?? 0) > 0 && (
						<div className="tariff-enough" key="enough">
							<div className="tariff-block-label">Токенов хватит на</div>
							<ul className="tariff-enough-cells">
								{t.enough!.map((c, i) => {
									// Ховер-кроссфейд в модель-эталон: юзер видит, КАКОЙ
									// нейронки хватит на это число (иконка бренда — фронт-
									// словарь detectBrand, имя — с бэка). Эталон без модели
									// (ручная цена) остаётся простой ячейкой.
									const brand = c.adapter_name && c.model_id ? detectBrand(c.adapter_name, c.model_id) : null
									return (
										<li key={i} className={brand ? 'tariff-enough-cell--model' : undefined}>
											<div className="tariff-enough-cell-base">
												<b>{c.count.toLocaleString('ru-RU')}</b>
												<span>{c.label}</span>
											</div>
											{brand && (
												<div className="tariff-enough-cell-model" aria-hidden="true">
													<ModelIcon icon={brand.icon} name={shortModelName(c.model_name || brand.name)} size={20} />
													<span>{shortModelName(c.model_name || brand.name)}</span>
												</div>
											)}
										</li>
									)
								})}
							</ul>
							<p className="tariff-enough-note">Что-то одно из этого — или любая комбинация: токены общие.</p>
						</div>
					)
					const scopedFree = (t.free_models ?? []).filter((m) => scopeAllows(m.applies_to))
					const freeBlock = scopedFree.length > 0 ? (
						<div className="tariff-freeblock" key="free">
							<div className="tariff-freeblock-head">Бесплатные нейросети</div>
							<ul>
								{scopedFree.map((m, i) => (
									<li key={i}>
										<span className="tariff-freeblock-name">
											{m.name}
											{m.params_label ? ` (${m.params_label})` : ''}
											{showScopeTags && m.applies_to === 'subscription' && <em className="tariff-scope-tag">по подписке</em>}
											{showScopeTags && m.applies_to === 'one_time' && <em className="tariff-scope-tag">разовым</em>}
										</span>
										<span className="tariff-freeblock-limit">до {m.daily_limit} в день</span>
									</li>
								))}
							</ul>
							<p className="tariff-freeblock-note">Токены не списываются</p>
						</div>
					) : emptyFreeNote ? (
						<div className="tariff-freeblock off" key="free">
							<div className="tariff-freeblock-head">Бесплатные нейросети</div>
							<p className="tariff-freeblock-note">{emptyFreeNote}</p>
						</div>
					) : null
					const perksBlock = (
						<Fragment key="perks">
							{allFeatures.map((f, i) => (
								<Fragment key={i}>
									{i > 0 && <div className="tariff-feature-divider" />}
									<div className="tariff-feature">
										<img src="/icons/gray_check.svg" alt="" width="14" height="14" />
										<span dangerouslySetInnerHTML={{ __html: f.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>') }} />
									</div>
								</Fragment>
							))}
						</Fragment>
					)
					const systemBlocks: Record<string, React.ReactNode> = {
						enough: enoughBlock, free: freeBlock, perks: perksBlock,
					}
					const layout = t.card_blocks?.length
						? t.card_blocks
						: ([{ kind: 'enough' }, { kind: 'free' }, { kind: 'perks' }, { kind: 'included' }] as CardBlock[])
					// Перки открытого режима: тинт-контейнер из payload.
					const wrapSystem = (b: CardBlock, node: React.ReactNode) => {
						if (!node) return null
						if (b.kind === 'perks') {
							const st = b.payload?.style
							if (st === 'violet' || st === 'mint') {
								return <div className={`tariff-custombox tariff-custombox--${st}`}>{node}</div>
							}
						}
						return node
					}
					// Аккордеон-блоки НЕ живут в потоке: каждый — кнопка в футере
					// карточки, контент раскрывается поповером ПОВЕРХ неё (решение
					// владельца 2026-08-08 — раскрытие не двигает сетку тарифов).
					const accordions: { key: string; title: string; badge: React.ReactNode; content: React.ReactNode }[] = []
					const pushAcc = (key: string, title: string, badge: React.ReactNode, content: React.ReactNode) => {
						accordions.push({ key, title, badge, content })
						return null
					}
					const includedKv = (t.included?.length ?? 0) > 0 ? (
						<ul className="tariff-popover-kv">
							{t.included!.map((g) => (
								<li key={g.category}>
									<span>{INCLUDED_CATEGORY_LABELS[g.category] ?? g.category}</span>
									<span>{includedShort(g.names)}</span>
								</li>
							))}
						</ul>
					) : null
					const rendered = layout.map((b, bi) => {
						// «Кому» блока: чужой для режима/зрителя — не рендерим вовсе.
						if (!scopeAllows(b.applies_to)) return null
						if (b.kind === 'included') {
							if (!includedKv) return null
							if (b.payload?.collapsed === false) {
								return (
									<div className="tariff-included tariff-included--open" key={bi}>
										<div className="tariff-block-label">{b.title || 'Какие модели входят'}</div>
										{includedKv}
									</div>
								)
							}
							return <Fragment key={bi}>{pushAcc(`b${bi}`, b.title || 'Какие модели входят', null, includedKv)}</Fragment>
						}
						if (b.kind === 'perks' && b.payload?.collapsed && systemBlocks.perks) {
							const st = b.payload?.style
							const inner = st === 'violet' || st === 'mint'
								? <div className={`tariff-custombox tariff-custombox--${st}`}>{systemBlocks.perks}</div>
								: systemBlocks.perks
							return <Fragment key={bi}>{pushAcc(`b${bi}`, b.title || 'Что входит в тариф', null, inner)}</Fragment>
						}
						if (b.kind !== 'custom') return <Fragment key={bi}>{wrapSystem(b, systemBlocks[b.kind])}</Fragment>
						// Системные строки валидны без текста (авто); каждая тихо
						// выпадает, когда её свойства у текущей подачи нет:
						// перенос — у разового/0%, персонажи — при лимите 0.
						const carryoverText = carryoverBenefit(t)
						const charactersText = characterFeature(charLimitEff, charFreeEff)
						const lines = (b.payload?.lines ?? []).filter((l) =>
							l.kind === 'carryover'
								? !asOneTime && !!carryoverText
								: l.kind === 'characters'
									? !!charactersText
									: l.title || l.text,
						)
						if (!lines.length) return null
						const style = b.payload?.style === 'mint' ? 'mint' : b.payload?.style === 'plain' ? 'plain' : 'violet'
						const isRows = b.payload?.layout === 'rows'
						const boxMarker = b.payload?.marker && b.payload.marker in CUSTOM_MARKER_GLYPHS ? b.payload.marker : 'plus'
						// Свой маркер строки перебивает маркер бокса
						const markerElFor = (l: { marker?: string }) => {
							const kind = l.marker && l.marker in CUSTOM_MARKER_GLYPHS ? l.marker : boxMarker
							return CUSTOM_MARKER_GLYPHS[kind] ? (
								<span className={`tariff-custombox-marker tariff-custombox-marker--${kind}`} aria-hidden="true">
									{CUSTOM_MARKER_GLYPHS[kind]}
								</span>
							) : null
						}
						const scopeBadgeEl =
							!showScopeTags ? null
								: b.applies_to === 'subscription' ? <em className="tariff-scope-tag">по подписке</em>
								: b.applies_to === 'one_time' ? <em className="tariff-scope-tag">разовым</em>
								: null
						const body = (
							// tariff-custombox-lines: в аккордеоне строки держат язык
							// бонус-бокса (влево, тонкий текст), а не «ключ — значение»
							<ul className={isRows ? 'tariff-custombox-rows' : 'tariff-custombox-lines'}>
								{lines.map((l, li) => {
									// «Персонажи · авто»: значения по подаче (подписка /
									// разово), шаблоны {limit}/{free}; без шаблонов —
									// стандартная фраза с жирной частью.
									if (l.kind === 'characters') {
										const sub = (s: string) =>
											s.replace(/\{limit\}/g, String(charLimitEff)).replace(/\{free\}/g, String(charFreeEff))
										// Разовая подача берёт СВОИ шаблоны (пусто = как у подписки)
										const tplTitle = asOneTime ? l.otTitle ?? l.title : l.title
										const tplText = asOneTime ? l.otText ?? l.text : l.text
										const bold = sub((tplTitle || '').trim())
										const custom = sub((tplText || '').trim())
										if (isRows) {
											return (
												<li key={li}>
													<span className="tariff-customrow-key">{bold || 'Аватары'}</span>
													<span className="tariff-customrow-val">
														{custom || (charactersText || '').replace(/\*\*/g, '')}
													</span>
												</li>
											)
										}
										return (
											<li key={li}>
												{markerElFor(l)}
												{bold || custom ? (
													<span>
														{bold && <b>{bold}</b>}
														{bold && custom ? ' — ' : ''}
														{custom}
													</span>
												) : (
													<span dangerouslySetInnerHTML={{ __html: (charactersText || '').replace(/\*\*(.*?)\*\*/g, '<b>$1</b>') }} />
												)}
											</li>
										)
									}
									// «Перенос · авто»: видимость системная, формулировка —
									// админская (l.text с живой подстановкой {pct}) либо
									// стандартная вычисленная фраза.
									if (l.kind === 'carryover') {
										const custom = (l.text || '').trim()
										const lineText = custom
											? custom.replace(/\{pct\}/g, String(t.carryover_percent ?? 0))
											: carryoverText
										// «Жирное» — как у обычной строки (l.title, тоже с {pct})
										const bold = (l.title || '').trim().replace(/\{pct\}/g, String(t.carryover_percent ?? 0))
										return isRows ? (
											<li key={li}>
												<span className="tariff-customrow-key">{bold || 'Перенос токенов'}</span>
												<span className="tariff-customrow-val">{lineText}</span>
											</li>
										) : (
											<li key={li}>
												{markerElFor(l)}
												<span>
													{bold && <b>{bold}</b>}
													{bold && lineText ? ' — ' : ''}
													{lineText}
												</span>
											</li>
										)
									}
									return isRows ? (
										<li key={li}>
											<span className="tariff-customrow-key">{l.title}</span>
											<span className="tariff-customrow-val">{l.text}</span>
										</li>
									) : (
										<li key={li}>
											{markerElFor(l)}
											<span>
												{l.title && <b>{l.title}</b>}
												{l.title && l.text ? ' — ' : ''}
												{l.text}
											</span>
										</li>
									)
								})}
							</ul>
						)
						// Аккордеон-кастом: кнопка в футере + поповер (тинт-стили
						// внутри поповера не применяются — нейтральная подложка).
						if (b.payload?.collapsed) {
							return <Fragment key={bi}>{pushAcc(`b${bi}`, b.title || 'Подробнее', scopeBadgeEl, body)}</Fragment>
						}
						return (
							<div className={`tariff-custombox tariff-custombox--${style}`} key={bi}>
								{(b.title || scopeBadgeEl) && (
									<div className="tariff-block-label">{b.title}{scopeBadgeEl}</div>
								)}
								{body}
							</div>
						)
					})
					const openAcc = openPop ? accordions.find((a) => a.key === openPop) : null
					return (
						<>
							{rendered}
							{accordions.length > 0 && (
								<div className="tariff-foot" onClick={(e) => e.stopPropagation()}>
									{openAcc && (
										<>
											{/* Прозрачный оверлей: клик снаружи карточки закрывает */}
											<div className="tariff-popover-backdrop" onClick={() => setOpenPop(null)} aria-hidden="true" />
											<div className="tariff-popover" role="dialog" aria-label={openAcc.title}>
												<div className="tariff-popover-title">{openAcc.title}{openAcc.badge}</div>
												{openAcc.content}
											</div>
										</>
									)}
									{accordions.map((a) => (
										<button
											key={a.key}
											type="button"
											className={`tariff-foot-btn${openPop === a.key ? ' is-open' : ''}`}
											aria-expanded={openPop === a.key}
											onClick={() => setOpenPop(openPop === a.key ? null : a.key)}
										>
											{a.title}
										</button>
									))}
								</div>
							)}
						</>
					)
				})()}
			</div>
		</div>
	)
}
